import * as timeZoneConverter from '@bingads-webui-universal/time-zone-converter';

let initialized = false;
let i18n;

class OneDay {
  constructor(date) {
    this.date = (date instanceof Date) ?
      date :
      new Date();
  }

  /**
   * Get givin time zone - UTC offset in millisecond
   * @param {string|null} timeZone -
   *   i.e. PacificTimeUSCanadaTijuana, or null for local time zone
   * @param {Date} date -
   *   A time zone may have different offsets to UTC because of daylight saving time
   *
   * @returns {number} - offset in millisecond
   */
  static getOffset(timeZone, date) {
    if (!timeZone) {
      // local - UTC offset
      return (date).getTimezoneOffset() * -1 * 60 * 1000;
    }

    // IANA time zone, i.e. US/Pacific
    const ianaTimeZone = timeZoneConverter.toIANATimeZone(timeZone);
    // i.e. -07:00
    const offsetString = i18n.formatDate(date, { timeZone: ianaTimeZone, raw: 'xxx' });
    const offsetFactor = offsetString[0] === '-' ? -1 : 1;
    const offsetHour = Number(offsetString.substr(1, 2));
    const offsetMinute = Number(offsetString.substr(4, 2));
    const offsetMilliSecond = ((offsetHour * 60) + offsetMinute) * 60 * 1000 * offsetFactor;

    return offsetMilliSecond;
  }

  /**
   * Convert from source time zone to target time zone
   * @param {Date} date - date at source time zone
   * @param {string|null} sourceTimeZone - source time zone
   * @param {string|null} targetTimeZone - target time zone
   *
   * @returns {Date} - date at target time zone
   */
  static convert(date, sourceTimeZone, targetTimeZone) {
    const totalOffset =
      OneDay.getOffset(sourceTimeZone, date) - OneDay.getOffset(targetTimeZone, date);

    return new Date(date.getTime() - totalOffset);
  }

  /**
   * Add some offset for the target time zone
   * @param {string|null} targetTimeZone - target time zone
   *
   * @returns {object} - this
   */
  zone(targetTimeZone) {
    this.date = OneDay.convert(this.date, null, targetTimeZone);
    return this;
  }

  add(numOfDay) {
    this.date.setDate(this.date.getDate() + numOfDay);
    return this;
  }

  firstSecond() {
    this.date.setHours(0, 0, 0, 0);
    return this;
  }

  lastSecond() {
    this.date.setHours(23, 59, 59, 998);
    return this;
  }

  /**
   * Convert to Date instance
   *
   * @returns {Date} date
   */
  toDate() {
    return new Date(this.date);
  }

  /**
   * Format to date string, to communicate with middle tier
   *
   * @returns {string} - date string
   */
  toServer() {
    return i18n.formatDate(this.date, { timeZone: null, raw: 'yyyy-MM-dd' });
  }
}

function oneDay(date) {
  return new OneDay(date);
}

/**
 * Parse datetime string from middle tier to Date instance
 * @param {string|null} datetime - datetime string, or null for no end date
 *
 * @returns {Date|null} - Date instance, or null for no end date
 */
function parseDate(datetime) {
  if (!datetime) {
    return null;
  }

  const date = datetime.substr(0, 10).replace(/-/g, '/');

  return new Date(date);
}

/**
 * Re-format datetime string from middle tier to user-friendly date string
 * @param {string|null} datetime - datetime string, or null for no end date
 *
 * @returns {string} - user-friendly date string
 */
function reformatDate(datetime) {
  const date = parseDate(datetime);

  if (!date) {
    return i18n.getString('None');
  }

  return i18n.formatDate(date, { timeZone: null });
}

const utils = {
  oneDay,
  reformatDate,
  parseDate,
};

export function getDateUtils(deps) {
  if (!initialized) {
    ({ i18n } = deps);
    initialized = true;
  }

  return utils;
}
